import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDeadLetterEntry, useResolveDeadLetter } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import {
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Clock,
  Server,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { DeadLetterResolvedStatus } from '../types';

const statusColors: Record<DeadLetterResolvedStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  retried: 'bg-blue-100 text-blue-800',
  ignored: 'bg-gray-100 text-gray-800',
  fixed: 'bg-green-100 text-green-800',
};

/**
 * How old a `pending` record has to be before "pending" stops being true (#131).
 *
 * A record this old is not awaiting triage, it is abandoned — and on 2026-08-06
 * one was reported as a live production failure when it had failed four days
 * earlier, for a bug fixed since, on an application that had already recovered.
 *
 * Three days rather than one: the DLQ is triaged by a human, not a scheduler,
 * so a record sitting over a weekend is normal and flagging it would train the
 * reader to ignore the flag — the same way the deploy false-alarm in #137 does.
 */
const STALE_PENDING_MS = 3 * 24 * 60 * 60 * 1000;

export default function DeadLetterDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const entryId = parseInt(id || '0', 10);

  const { data: entry, isLoading, error, refetch } = useDeadLetterEntry(entryId);
  const resolveMutation = useResolveDeadLetter();
  const { showToast } = useToast();

  const [showPayload, setShowPayload] = useState(false);
  const [notes, setNotes] = useState('');

  const handleResolve = async (status: string) => {
    if (!window.confirm(`Mark this entry as "${status}"?`)) return;
    try {
      await resolveMutation.mutateAsync({
        id: entryId,
        resolved: status,
        notes: notes || undefined,
      });
      showToast(`Entry marked as "${status}"`, 'success');
      refetch();
    } catch (err) {
      console.error('Resolve failed:', err);
      showToast('Failed to resolve entry', 'error');
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/dead-letter')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dead Letter Queue
        </button>
        <div className="card p-6">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
            <p className="mt-4 text-red-600">
              Error loading entry: {error.message}
            </p>
            <button onClick={() => refetch()} className="btn btn-primary mt-4">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !entry) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/dead-letter')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dead Letter Queue
        </button>
        <div className="card p-6">
          <div className="text-center py-12">
            <RefreshCw className="mx-auto h-8 w-8 animate-spin text-gray-400" />
            <p className="mt-2 text-gray-500">Loading entry...</p>
          </div>
        </div>
      </div>
    );
  }

  // Only `pending` can be stale: the other statuses record a decision someone
  // already made, and calling a deliberately-ignored record "stale" would be
  // noise. Computed after the guards above so `entry` is known non-null.
  const isStalePending =
    entry.resolved === 'pending' &&
    Date.now() - new Date(entry.failedAt).getTime() > STALE_PENDING_MS;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dead-letter')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Dead Letter Queue
      </button>

      {/* Header */}
      {/*
        `data-testid` is load-bearing, not decoration: the #131 tests assert the
        failure age is inside THIS block rather than merely somewhere on the
        page (the Timeline card further down also renders it). Scoping by DOM
        traversal instead would silently widen to the whole page the first time
        anyone wraps the heading in another div, and the placement assertions
        would then pass with this block deleted.
      */}
      <div data-testid="dlq-header" className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                Dead Letter #{entry.id}
              </h1>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                  statusColors[entry.resolved as DeadLetterResolvedStatus] || statusColors.pending
                }`}
              >
                {entry.resolved}
              </span>
            </div>
            {/*
              #131: the age belongs HERE, next to the status, not only in the
              Timeline card further down. A reader takes "pending" from this
              header and stops; without an age beside it, a four-day-old record
              for an already-fixed bug is indistinguishable from a live failure.
            */}
            <p className="text-sm text-gray-600 mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <Clock className="h-4 w-4 text-gray-400" />
              <span>
                Failed{' '}
                <span className="font-medium text-gray-900">
                  {formatDistanceToNow(new Date(entry.failedAt), { addSuffix: true })}
                </span>
              </span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">
                {new Date(entry.failedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              {isStalePending && (
                <span
                  className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800"
                  title="Still marked pending, but old enough that it is probably abandoned rather than awaiting triage. Check whether the underlying bug has since been fixed before treating this as a live failure."
                >
                  stale — check before treating as live
                </span>
              )}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Original Job: {entry.originalJobName || 'unknown'} (ID: {entry.originalJobId})
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Queue: <span className="font-medium">{entry.originalQueue}</span>
            </p>
          </div>
          <div className="flex gap-2">
            {entry.resolved === 'pending' && (
              <>
                <button
                  onClick={() => handleResolve('fixed')}
                  className="btn btn-primary"
                  disabled={resolveMutation.isPending}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Mark Fixed
                </button>
                <button
                  onClick={() => handleResolve('retried')}
                  className="btn btn-secondary"
                  disabled={resolveMutation.isPending}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Mark Retried
                </button>
                <button
                  onClick={() => handleResolve('ignored')}
                  className="btn btn-secondary"
                  disabled={resolveMutation.isPending}
                >
                  Ignore
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Error Message */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Error Details
            </h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <pre className="text-sm text-red-800 whitespace-pre-wrap break-words font-mono">
                {entry.error || 'No error message'}
              </pre>
            </div>
          </div>

          {/* Original Payload */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowPayload(!showPayload)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Server className="h-5 w-5 text-gray-500" />
                Original Payload
              </h2>
              {showPayload ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>
            {showPayload && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs text-gray-100">
                  {JSON.stringify(entry.originalData, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Notes */}
          {entry.resolved === 'pending' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this failure (optional)..."
                className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-blue-500 focus:ring-blue-500"
                rows={3}
              />
            </div>
          )}

          {/* Existing notes display */}
          {entry.notes && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Resolution Notes</h2>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.notes}</p>
            </div>
          )}
        </div>

        {/* Right Column - Metadata */}
        <div className="lg:col-span-1 space-y-6">
          {/* Metadata Card */}
          <div className="card p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Details</h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">DLQ Record ID</dt>
                <dd className="font-medium text-gray-900">{entry.id}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Original Queue</dt>
                <dd className="font-medium text-gray-900">{entry.originalQueue}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Original Job ID</dt>
                <dd className="font-mono text-xs font-medium text-gray-900 break-all">
                  {entry.originalJobId}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Original Job Name</dt>
                <dd className="font-medium text-gray-900">
                  {entry.originalJobName || 'unknown'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Attempts Made</dt>
                <dd className="font-medium text-gray-900">{entry.attemptsMade}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Resolution Status</dt>
                <dd>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      statusColors[entry.resolved as DeadLetterResolvedStatus] || statusColors.pending
                    }`}
                  >
                    {entry.resolved}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Timestamps Card */}
          <div className="card p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-500" />
              Timeline
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Failed At</dt>
                <dd className="font-medium text-gray-900">
                  {new Date(entry.failedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </dd>
                <dd className="text-xs text-gray-400">
                  {formatDistanceToNow(new Date(entry.failedAt), { addSuffix: true })}
                </dd>
              </div>
              {entry.processedAt && (
                <div>
                  <dt className="text-gray-500">Processed At</dt>
                  <dd className="font-medium text-gray-900">
                    {new Date(entry.processedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Created At</dt>
                <dd className="font-medium text-gray-900">
                  {new Date(entry.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Updated At</dt>
                <dd className="font-medium text-gray-900">
                  {new Date(entry.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
